import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Tag,
  Modal,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Descriptions,
  Divider,
  DatePicker,
  Dropdown,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  UndoOutlined,
  ReloadOutlined,
  GroupOutlined,
  DownOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import type { ColumnsType } from 'antd/es/table';
import {
  JournalEntry,
  Journal,
  CreateJournalEntryInput,
  journalService
} from '@/services/journalService';
import { Account } from '@/services/accountService';
import JournalEntryForm from './JournalEntryForm';
import JournalEntryBulkUpload from './JournalEntryBulkUpload';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface JournalEntriesProps {
  journals: Journal[];
  accounts: Account[];
}

const JournalEntries: React.FC<JournalEntriesProps> = ({ journals: initialJournals, accounts }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [journals, setJournals] = useState<Journal[]>(initialJournals);

  // Modal states
  const [entryModalVisible, setEntryModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [reverseModalVisible, setReverseModalVisible] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [journalFilter, setJournalFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [groupBy, setGroupBy] = useState<string | null>(null);

  // Helper function to get group value
  const getGroupValue = (entry: JournalEntry, groupByField: string): string => {
    switch (groupByField) {
      case 'state':
        return entry.state || 'Unknown';
      case 'journal':
        return entry.journal?.name || 'Unknown Journal';
      case 'month':
        return dayjs(entry.date).format('MMMM YYYY');
      case 'user':
        return entry.createdBy || 'Unknown User';
      default:
        return 'Ungrouped';
    }
  };

  // Fetch journals
  const fetchJournals = async () => {
    try {
      const journalsData = await journalService.getActiveJournals();
      setJournals(journalsData);
    } catch (error: any) {
      console.error('Failed to fetch journals:', error);
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchJournals();
  }, []);

  // Update journals when prop changes
  useEffect(() => {
    setJournals(initialJournals);
  }, [initialJournals]);

  useEffect(() => {
    // Apply filters
    let filtered = [...entries];

    // State filter
    if (stateFilter !== 'all') {
      filtered = filtered.filter(entry => entry.state === stateFilter);
    }

    // Journal filter
    if (journalFilter !== 'all') {
      filtered = filtered.filter(entry => entry.journalId === journalFilter);
    }

    // Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].startOf('day');
      const endDate = dateRange[1].endOf('day');
      filtered = filtered.filter(entry => {
        const entryDate = dayjs(entry.date);
        return entryDate.isAfter(startDate) && entryDate.isBefore(endDate);
      });
    }

    // Search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(entry =>
        entry.name.toLowerCase().includes(search) ||
        (entry.ref && entry.ref.toLowerCase().includes(search)) ||
        (entry.narration && entry.narration.toLowerCase().includes(search)) ||
        ((entry as any).partnerName && (entry as any).partnerName.toLowerCase().includes(search)) ||
        (entry.chequeNo && entry.chequeNo.toLowerCase().includes(search)) ||
        (entry.invoiceNo && entry.invoiceNo.toLowerCase().includes(search))
      );
    }

    // Apply grouping
    if (groupBy) {
      filtered = filtered.sort((a, b) => {
        const groupA = getGroupValue(a, groupBy);
        const groupB = getGroupValue(b, groupBy);
        return groupA.localeCompare(groupB);
      });

      // Insert group header rows
      const withHeaders: any[] = [];
      let currentGroup = '';

      filtered.forEach((entry) => {
        const group = getGroupValue(entry, groupBy);
        if (group !== currentGroup) {
          currentGroup = group;
          withHeaders.push({
            id: `group-${group}`,
            isGroupHeader: true,
            groupName: group,
            groupItems: filtered.filter(e => getGroupValue(e, groupBy) === group)
          });
        }
        withHeaders.push(entry);
      });

      filtered = withHeaders;
    }

    setFilteredEntries(filtered);
  }, [entries, searchText, stateFilter, journalFilter, dateRange, groupBy]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const data = await journalService.getJournalEntries();
      setEntries(data);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch journal entries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntry = () => {
    console.log('🔘 New Entry button clicked');
    console.log('Journals:', journals.length);
    console.log('Accounts:', accounts.length);

    // Validate data is loaded
    if (journals.length === 0) {
      message.error('No journals available. Please create journals first.');
      console.error('❌ No journals loaded');
      return;
    }

    if (accounts.length === 0) {
      message.error('No accounts available. Please create accounts first.');
      console.error('❌ No accounts loaded');
      return;
    }

    console.log('✅ Data validation passed');
    setSelectedEntry(null);
    setIsEditing(false);
    setEntryModalVisible(true);
    console.log('✅ Modal visibility set to true');
  };

  const handleEditEntry = (entry: JournalEntry) => {
    // Drafts and posted entries are both editable (#20). A cancelled entry is
    // not worth editing, and the server refuses it.
    if (entry.state === 'CANCELLED') {
      message.warning('Cancelled entries cannot be edited');
      return;
    }
    // Editing an entry a document is posted to is allowed, but the document
    // will not follow the change, so say so once rather than silently.
    if (entry.state === 'POSTED' && (entry as any).isArApRecord) {
      message.warning(
        'A document is posted to this entry. Your changes will not carry across to it — reverse the entry instead if you need both to agree.'
      );
    }
    setSelectedEntry(entry);
    setIsEditing(true);
    setEntryModalVisible(true);
  };

  const handleViewEntry = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setViewModalVisible(true);
  };

  const handleSubmitEntry = async (values: CreateJournalEntryInput) => {
    setLoading(true);
    try {
      if (isEditing && selectedEntry) {
        await journalService.updateJournalEntry(selectedEntry.id, values);
        message.success('Journal entry updated successfully');
      } else {
        await journalService.createJournalEntry(values);
        message.success('Journal entry created successfully');
      }
      setEntryModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to save journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAndPostEntry = async (values: CreateJournalEntryInput) => {
    setLoading(true);
    try {
      let entryId: string;

      if (isEditing && selectedEntry) {
        const updatedEntry = await journalService.updateJournalEntry(selectedEntry.id, values);
        entryId = updatedEntry.id;
        message.success('Journal entry updated successfully');
      } else {
        const newEntry = await journalService.createJournalEntry(values);
        entryId = newEntry.id;
        message.success('Journal entry created successfully');
      }

      // Post the entry immediately
      await journalService.postJournalEntry(entryId);
      message.success('Journal entry posted successfully');

      setEntryModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to save and post journal entry');
    } finally {
      setLoading(false);
    }
  };

  /**
   * The date an entry belongs under. Accounting Date is what drives the GL
   * period, so it is what every report should show — the entry's own date is
   * only a fallback for entries that never had one (client feedback #22).
   * Mirrors resolveDisplayDate in GeneralLedger.tsx.
   */
  const displayDate = (e: any) => e?.accountingDate ?? e?.date;

  const handlePostEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.postJournalEntry(entry.id);
      message.success('Journal entry posted successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to post journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.cancelJournalEntry(entry.id);
      message.success('Journal entry cancelled successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleReverseEntry = async (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setReverseModalVisible(true);
  };

  const handleConfirmReverse = async () => {
    if (!selectedEntry) return;

    setLoading(true);
    try {
      await journalService.reverseJournalEntry(selectedEntry.id, {
        reversalDate: new Date().toISOString(),
        narration: `Reversal of ${selectedEntry.name}`
      });
      message.success('Reversal entry created successfully (in DRAFT state)');
      setReverseModalVisible(false);
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to reverse journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entry: JournalEntry) => {
    setLoading(true);
    try {
      await journalService.deleteJournalEntry(entry.id);
      message.success('Journal entry deleted successfully');
      fetchEntries();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Filter out group header rows
    const actualEntries = filteredEntries.filter((e: any) => !e.isGroupHeader);
    if (actualEntries.length === 0) {
      message.warning('No data to export');
      return;
    }

    const wb = XLSX.utils.book_new();

    // Sheet 1: Journal Entry Summary
    const summaryRows = actualEntries.map(e => ({
      'Entry #': e.name || '',
      'Date': displayDate(e) ? dayjs(displayDate(e)).format('YYYY-MM-DD') : '',
      'Reference': e.ref || '',
      'Partner/Customer': (e as any).partnerName || '',
      'Description': e.narration || '',
      'Journal': e.journal?.name || '',
      // Department lives on the lines, so roll the distinct values up to the
      // entry. It was only ever on the "Line Items" sheet, and since Excel opens
      // "Journal Entries" first the column looked missing entirely.
      // An entry can span several departments, hence the comma-separated list.
      'Department': Array.from(new Set(
        (e.lines || [])
          // Typed name first; lines saved before the Department field was
          // unlinked from HR still resolve through the linked department.
          .map((l: any) => l.departmentName || (l.department ? `${l.department.code} - ${l.department.name}` : ''))
          .filter(Boolean)
      )).join(', '),
      'Total Debit': e.lines ? e.lines.reduce((sum: number, l: any) => sum + Number(l.debit || 0), 0) : 0,
      'Total Credit': e.lines ? e.lines.reduce((sum: number, l: any) => sum + Number(l.credit || 0), 0) : 0,
      'Status': (e.state || '').toUpperCase(),
    }));
    const ws1 = XLSX.utils.json_to_sheet(summaryRows);
    // Widths in row-key order: Entry #, Date, Reference, Partner/Customer,
    // Description, Journal, Department, Total Debit, Total Credit, Status.
    ws1['!cols'] = [
      { wch: 18 }, { wch: 12 }, { wch: 20 }, { wch: 20 },
      { wch: 30 }, { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 14 },
      { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, 'Journal Entries');

    // Sheet 2: Line Items
    const lineRows: Record<string, any>[] = [];
    actualEntries.forEach(e => {
      if (e.lines && e.lines.length > 0) {
        e.lines.forEach((line: any) => {
          lineRows.push({
            'Entry #': e.name || '',
            'Date': displayDate(e) ? dayjs(displayDate(e)).format('YYYY-MM-DD') : '',
            'Journal': e.journal?.name || '',
            'Account Code': line.account?.code || '',
            'Account Name': line.account?.name || '',
            'Description': line.name || '',
            // Line-level counterparty, falling back to the entry's partner so a
            // row is never blank just because the partner was tagged once on the
            // header rather than repeated on every line.
            'Partner/Customer': line.partnerName || line.partnerId || (e as any).partnerName || '',
            'Department': line.departmentName
              || (line.department ? `${line.department.code} - ${line.department.name}` : ''),
            'Debit': Number(line.debit || 0),
            'Credit': Number(line.credit || 0),
          });
        });
      }
    });
    const ws2 = XLSX.utils.json_to_sheet(lineRows.length > 0 ? lineRows : [{ 'Note': 'No line items found' }]);
    // Column widths in row-key order: Entry #, Date, Journal, Account Code,
    // Account Name, Description, Partner/Customer, Department, Debit, Credit.
    ws2['!cols'] = [
      { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 12 },
      { wch: 30 }, { wch: 25 }, { wch: 22 }, { wch: 20 }, { wch: 14 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, 'Line Items');

    XLSX.writeFile(wb, `Journal_Entries_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success(`Exported ${actualEntries.length} journal entries with line items`);
  };

  const getStateTag = (state: string) => {
    const stateColors: Record<string, string> = {
      DRAFT: 'default',
      POSTED: 'success',
      CANCELLED: 'error'
    };
    return <Tag color={stateColors[state] || 'default'}>{state}</Tag>;
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Entry #',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      // Numbers read BNK2/2026/08/0003, so a plain text sort puts 0010 before
      // 0009 only when the widths differ — they don't here, but they do against
      // the imported legacy names (INV-0754). numeric:true handles both, and
      // group headers are left where they are rather than sorted into the list.
      sorter: (a: any, b: any) => {
        if (a.isGroupHeader || b.isGroupHeader) return 0;
        return (a.name || '').localeCompare(b.name || '', undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      },
      render: (name: string, record: any) => {
        if (record.isGroupHeader) {
          return {
            children: (
              <div style={{
                fontWeight: 'bold',
                fontSize: '14px',
                color: '#1890ff',
                padding: '8px 0'
              }}>
                📁 {record.groupName} ({record.groupItems.length} entries)
              </div>
            ),
            props: {
              colSpan: 11 // Span across all columns (8 original + 3 new: Partner, Cheque No, Invoice No)
            }
          };
        }
        return <Text code>{name}</Text>;
      }
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(displayDate(a)).getTime() - new Date(displayDate(b)).getTime(),
      render: (_date: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return dayjs(displayDate(record)).format('YYYY-MM-DD');
      }
    },
    {
      title: 'Journal',
      key: 'journal',
      width: 150,
      render: (_, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return record.journal ? (
          <div>
            <Tag color="blue">{record.journal.code}</Tag>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.journal.name}
            </Text>
          </div>
        ) : null;
      }
    },
    {
      title: 'Reference',
      dataIndex: 'ref',
      key: 'ref',
      width: 120,
      render: (ref: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return ref || '-';
      }
    },
    {
      title: 'Partner',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: 120,
      render: (partnerName: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return partnerName || '-';
      }
    },
    {
      title: 'Cheque No',
      dataIndex: 'chequeNo',
      key: 'chequeNo',
      width: 100,
      render: (chequeNo: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return chequeNo || '-';
      }
    },
    {
      title: 'Invoice No',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 120,
      render: (invoiceNo: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return invoiceNo || '-';
      }
    },
    {
      title: 'Description',
      dataIndex: 'narration',
      key: 'narration',
      ellipsis: true,
      render: (narration: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return narration || '-';
      }
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (_, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        const total = record.lines.reduce((sum: number, line: any) => sum + Number(line.debit), 0);
        return <Text strong>${total.toFixed(2)}</Text>;
      }
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      filters: [
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Posted', value: 'POSTED' },
        { text: 'Cancelled', value: 'CANCELLED' }
      ],
      onFilter: (value, record) => record.state === value,
      render: (state: string, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return getStateTag(state);
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record: any) => {
        if (record.isGroupHeader) return { props: { colSpan: 0 } };
        return (
        <Space size={2} wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewEntry(record)}
          >
            View
          </Button>
          {record.state === 'DRAFT' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditEntry(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Post this entry?"
                description="Once posted, the entry cannot be edited."
                onConfirm={() => handlePostEntry(record)}
                okText="Post"
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  size="small"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                >
                  Post
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Delete this entry?"
                onConfirm={() => handleDeleteEntry(record)}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
          {record.state === 'POSTED' && (
            <>
              {/* Every posted entry can now be edited and cancelled (#20).
                  isArApRecord no longer hides anything — it warns, because
                  editing an entry a document is posted to leaves that document
                  and the ledger disagreeing, and only credit notes have a
                  foreign key that would stop a delete. */}
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditEntry(record)}
              >
                Edit
              </Button>
              <Button
                type="link"
                size="small"
                icon={<UndoOutlined />}
                onClick={() => handleReverseEntry(record)}
              >
                Reverse
              </Button>
              <Popconfirm
                title="Cancel this entry?"
                description={
                  record.isArApRecord
                    ? 'A document is posted to this entry. Cancelling it leaves that document and the ledger disagreeing — reversing it instead keeps both in step. It will be marked cancelled, and can then be deleted.'
                    : 'It will be marked cancelled, and can then be deleted.'
                }
                overlayStyle={record.isArApRecord ? { maxWidth: 380 } : undefined}
                onConfirm={() => handleCancelEntry(record)}
                okText="Cancel Entry"
                cancelText="No"
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                >
                  Cancel
                </Button>
              </Popconfirm>
              {record.isArApRecord && (
                <Tooltip title="An invoice, credit note, vendor bill or payment is posted to this entry. You can still edit it, but the document will not change with it — reverse it instead to keep both in step.">
                  <Tag color="warning" style={{ marginInlineStart: 4 }}>Linked to a document</Tag>
                </Tooltip>
              )}
            </>
          )}
          {record.state === 'CANCELLED' && (
            <Popconfirm
              title="Delete this entry?"
              description={
                record.isArApRecord
                  ? 'A document is posted to this entry. Deleting it will leave that document pointing at an entry that no longer exists. A copy is kept so it can be restored.'
                  : 'The entry and its lines are removed. A copy is kept so it can be restored.'
              }
              onConfirm={() => handleDeleteEntry(record)}
              okText="Delete"
              cancelText="No"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Journal Entries</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchEntries(); fetchJournals(); }}>
                Refresh
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setBulkUploadModalVisible(true)}
              >
                Bulk Upload
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>
                Export
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateEntry}
              >
                New Entry
              </Button>
            </Space>
          </Col>
        </Row>

        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Search entries..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                prefix={<EyeOutlined />}
              />
            </Col>
            <Col span={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by state"
                value={stateFilter}
                onChange={setStateFilter}
              >
                <Select.Option value="all">All States</Select.Option>
                <Select.Option value="DRAFT">Draft</Select.Option>
                <Select.Option value="POSTED">Posted</Select.Option>
                <Select.Option value="CANCELLED">Cancelled</Select.Option>
              </Select>
            </Col>
            <Col span={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by journal"
                value={journalFilter}
                onChange={setJournalFilter}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  { value: 'all', label: 'All Journals' },
                  ...journals.map(j => ({
                    value: j.id,
                    label: `${j.code} - ${j.name}`
                  }))
                ]}
              />
            </Col>
            <Col span={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
                format="YYYY-MM-DD"
                placeholder={['Start Date', 'End Date']}
                allowClear
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'state',
                      label: 'State',
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('state')
                    },
                    {
                      key: 'journal',
                      label: 'Journal',
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('journal')
                    },
                    {
                      key: 'month',
                      label: 'Entry Month',
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('month')
                    },
                    {
                      key: 'user',
                      label: 'Created By',
                      icon: <GroupOutlined />,
                      onClick: () => setGroupBy('user')
                    },
                    {
                      type: 'divider'
                    },
                    {
                      key: 'none',
                      label: 'No Grouping',
                      onClick: () => setGroupBy(null)
                    }
                  ]
                }}
              >
                <Button icon={<GroupOutlined />}>
                  Group By {groupBy ? `: ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}` : ''} <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        </Space>

        <Table
          dataSource={filteredEntries}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} entries`
          }}
          scroll={{ x: 1200 }}
          rowClassName={(record) => record.isGroupHeader ? 'group-header-row' : ''}
        />

        <style>{`
          .group-header-row {
            background-color: #f0f5ff !important;
            font-weight: 600;
          }
          .group-header-row:hover {
            background-color: #e6f0ff !important;
          }
          .group-header-row td {
            border-bottom: 2px solid #d9d9d9 !important;
          }
        `}</style>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={isEditing ? 'Edit Journal Entry' : 'Create Journal Entry'}
        open={entryModalVisible}
        onCancel={() => setEntryModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <JournalEntryForm
          entry={selectedEntry || undefined}
          journals={journals}
          accounts={accounts}
          onSubmit={handleSubmitEntry}
          onSubmitAndPost={handleSubmitAndPostEntry}
          onCancel={() => setEntryModalVisible(false)}
          loading={loading}
        />
      </Modal>

      {/* View Details Modal */}
      <Modal
        title="Journal Entry Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1100}
      >
        {selectedEntry && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Entry Number">{selectedEntry.name}</Descriptions.Item>
              <Descriptions.Item label="State">{getStateTag(selectedEntry.state)}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {dayjs(selectedEntry.date).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="Journal">
                {selectedEntry.journal?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Reference">{selectedEntry.ref || '-'}</Descriptions.Item>
              <Descriptions.Item label="Partner">{(selectedEntry as any).partnerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Cheque No">{selectedEntry.chequeNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="Invoice No">{selectedEntry.invoiceNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="Created By">{selectedEntry.createdBy}</Descriptions.Item>
              {selectedEntry.postedBy && (
                <>
                  <Descriptions.Item label="Posted By">{selectedEntry.postedBy}</Descriptions.Item>
                  <Descriptions.Item label="Posted At">
                    {dayjs(selectedEntry.postedAt).format('YYYY-MM-DD HH:mm')}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Description" span={2}>
                {selectedEntry.narration || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Entry Lines</Divider>

            <Table
              dataSource={selectedEntry.lines}
              size="small"
              pagination={false}
              rowKey="id"
              scroll={{ x: 900 }}
              columns={[
                {
                  title: 'Account',
                  key: 'account',
                  width: 220,
                  render: (_, line) => (
                    <div>
                      <Text strong>{line.account?.code}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {line.account?.name}
                      </Text>
                    </div>
                  )
                },
                {
                  title: 'Description',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string) => name || '-'
                },
                {
                  title: 'Debit',
                  dataIndex: 'debit',
                  key: 'debit',
                  align: 'right',
                  width: 140,
                  render: (debit: number) => (
                    <Text strong style={{ color: Number(debit) > 0 ? '#3f8600' : undefined, whiteSpace: 'nowrap' }}>
                      {Number(debit) > 0
                        ? `$${Number(debit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </Text>
                  )
                },
                {
                  title: 'Credit',
                  dataIndex: 'credit',
                  key: 'credit',
                  align: 'right',
                  width: 140,
                  render: (credit: number) => (
                    <Text strong style={{ color: Number(credit) > 0 ? '#cf1322' : undefined, whiteSpace: 'nowrap' }}>
                      {Number(credit) > 0
                        ? `$${Number(credit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </Text>
                  )
                }
              ]}
              summary={(data) => {
                const totalDr = data.reduce((s, l: any) => s + Number(l.debit || 0), 0);
                const totalCr = data.reduce((s, l: any) => s + Number(l.credit || 0), 0);
                const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong style={{ whiteSpace: 'nowrap' }}>{fmt(totalDr)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong style={{ whiteSpace: 'nowrap' }}>{fmt(totalCr)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </div>
        )}
      </Modal>

      {/* Reverse Confirmation Modal */}
      <Modal
        title="Reverse Journal Entry"
        open={reverseModalVisible}
        onOk={handleConfirmReverse}
        onCancel={() => setReverseModalVisible(false)}
        confirmLoading={loading}
      >
        <p>Are you sure you want to reverse this entry?</p>
        <p>
          A new journal entry will be created with debits and credits swapped. The new entry will be
          created in DRAFT state for your review before posting.
        </p>
      </Modal>

      {/* Bulk Upload Modal */}
      <JournalEntryBulkUpload
        visible={bulkUploadModalVisible}
        onClose={() => setBulkUploadModalVisible(false)}
        onSuccess={() => {
          fetchEntries();
          fetchJournals();
        }}
      />
    </div>
  );
};

export default JournalEntries;